import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CtaBanner } from "@/components/site/CtaBanner";
import { Reveal } from "@/components/site/Reveal";
import { ChevronRight, Clock, Calendar, ArrowRight, Phone, CheckCircle2 } from "lucide-react";
import { findBlogPost, blogPosts, type BlogSection, type BlogPost } from "@/lib/blog-data";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = findBlogPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    if (!post) return { meta: [{ title: "Guide" }] };
    const url = `https://blancopros.com/blog/${params.slug}`;
    return {
      meta: [
        { title: post.metaTitle },
        { name: "description", content: post.metaDescription },
        { name: "keywords", content: post.keywords.join(", ") },
        { property: "og:title", content: post.metaTitle },
        { property: "og:description", content: post.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.metaTitle },
        { name: "twitter:description", content: post.metaDescription },
        ...(post.heroImage
          ? [
              { property: "og:image", content: `https://blancopros.com${post.heroImage}` } as const,
              { name: "twitter:image", content: `https://blancopros.com${post.heroImage}` } as const,
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.metaDescription,
            datePublished: post.publishedAt,
            inLanguage: "en-US",
            mainEntityOfPage: url,
            ...(post.heroImage ? { image: `https://blancopros.com${post.heroImage}` } : {}),
            author: {
              "@type": "Person",
              name: "Thomas Blanco",
              jobTitle: "Founder, Blanco Pro Services",
              worksFor: { "@id": "https://blancopros.com/#organization" },
            },
            publisher: { "@id": "https://blancopros.com/#organization" },
            keywords: post.keywords.join(", "),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: post.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://blancopros.com/" },
              { "@type": "ListItem", position: 2, name: "Guides", item: "https://blancopros.com/blog" },
              { "@type": "ListItem", position: 3, name: post.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="pt-40 pb-20 text-center px-4">
      <h1 className="font-display text-4xl font-bold mb-4">Article not found</h1>
      <Link to="/blog" className="text-foreground underline">Back to Guides</Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="pt-40 pb-20 text-center px-4">
      <h1 className="font-display text-4xl font-bold mb-4">Something went wrong</h1>
      <button onClick={reset} className="text-foreground underline">Try again</button>
    </div>
  ),
  component: BlogPostPage,
});

function BlogPostPage() {
  const { post } = Route.useLoaderData();
  const related = blogPosts.filter((p) => p.slug !== post.slug);

  return (
    <>
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-hero">
        <div className="max-w-4xl mx-auto">
          <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-foreground transition-colors">Guides</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/usage/$category" params={{ category: post.category }} className="hover:text-foreground transition-colors capitalize">
              {post.category}
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground">Guide</span>
          </nav>

          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{post.heroTagline}</div>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-6">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-5 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {post.publishedAt}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.readTime}</span>
              <Link
                to="/usage/$category"
                params={{ category: post.category }}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-foreground/5 hover:bg-foreground/10 capitalize transition-colors"
              >
                {post.category} applications
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {post.heroImage && (
        <section className="px-4 sm:px-6 lg:px-8 -mt-2">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <img
                src={post.heroImage}
                alt={post.title}
                width={1600}
                height={900}
                className="w-full rounded-3xl shadow-elegant object-cover aspect-[16/9]"
              />
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-xl md:text-2xl font-display leading-relaxed text-foreground/90 mb-12 border-l-2 border-gold pl-6">
              {post.intro}
            </p>
          </Reveal>

          {post.inlineImage && (
            <Reveal>
              <img
                src={post.inlineImage}
                alt={`${post.title}, detail`}
                width={1280}
                height={1280}
                loading="lazy"
                className="w-full rounded-2xl shadow-soft object-cover aspect-square md:aspect-[16/10] mb-12"
              />
            </Reveal>
          )}


          <div className="space-y-12">
            {post.sections.map((s: BlogSection, i: number) => (
              <Reveal key={s.heading} delay={i * 60}>
                <article>
                  <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{s.heading}</h2>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-5">{s.body}</p>
                  {s.bullets && (
                    <ul className="space-y-2.5">
                      {s.bullets.map((b: string) => (
                        <li key={b} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 mt-0.5 text-gold shrink-0" />
                          <span className="text-foreground/90">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16">
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">Frequently asked questions</h2>
              <div className="space-y-4">
                {post.faqs.map((f: { q: string; a: string }) => (
                  <details key={f.q} className="group rounded-2xl border border-border bg-card p-6 open:shadow-soft transition-all">
                    <summary className="cursor-pointer font-display text-lg font-semibold list-none flex items-center justify-between gap-4">
                      {f.q}
                      <ChevronRight className="w-4 h-4 shrink-0 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-16 rounded-3xl border border-border bg-card p-8 md:p-10 text-center shadow-soft">
              <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Free Estimate</div>
              <h3 className="font-display text-3xl font-bold mb-3">Ready to start your project?</h3>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Licensed, insured, and serving New Jersey, Pennsylvania, and New York. Same-week consultations available.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="tel:9088956133" className="inline-flex items-center justify-center gap-2 bg-foreground text-background px-6 py-3 rounded-full text-sm font-medium hover:bg-gradient-gold hover:text-gold-foreground transition-all">
                  <Phone className="w-4 h-4" /> 908-895-6133
                </a>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 border-2 border-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-foreground hover:text-background transition-all">
                  Request a Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-12 rounded-3xl border border-border bg-card p-6 md:p-8 flex flex-col sm:flex-row items-start gap-5">
              <div className="w-16 h-16 rounded-full bg-gradient-gold flex items-center justify-center font-display text-2xl font-bold text-gold-foreground shadow-gold shrink-0">
                TB
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Written by</div>
                <h3 className="font-display text-xl font-bold mb-2">Thomas Blanco, Founder, Blanco Pro Services</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Thomas founded Blanco Pro Services in New Jersey and is currently pursuing a degree in Business Administration. He brings hands-on experience in hardscaping and concrete plus two years of professional residential demolition, and writes from the field, not from a marketing desk.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="font-display text-3xl font-bold mb-8">Keep reading</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6">
            {related.map((r: BlogPost, i: number) => (
              <Reveal key={r.slug} delay={i * 80}>
                <Link
                  to="/blog/$slug"
                  params={{ slug: r.slug }}
                  className="group block h-full rounded-2xl border border-border bg-card p-6 hover:shadow-elegant hover:-translate-y-1 transition-all"
                >
                  <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">{r.heroTagline}</div>
                  <h3 className="font-display text-xl font-bold mb-2 leading-tight">{r.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{r.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-foreground/70 group-hover:gap-2 transition-all">
                    Read <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
